import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Mail, Phone, MapPin, Youtube, ShoppingBag, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarketplaceIcon = ({ name, size = 16 }) => {
  const icons = {
    amazon: <span className="font-bold text-xs" style={{letterSpacing: '-0.5px'}}>a</span>,
    flipkart: <span className="font-bold text-xs">F</span>,
    meesho: <span className="font-bold text-xs">M</span>,
    indiamart: <span className="font-bold text-xs">IM</span>,
  };
  return <span className="w-5 h-5 flex items-center justify-center">{icons[name] || <ExternalLink size={size} />}</span>;
};

export const Footer = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (e) { console.error(e); }
    };
    fetchSettings();
  }, []);

  const s = settings || {};
  const email = s.contactEmail || 'contact@zindallighting.com';
  const phone = s.whatsappNumber || '9874773801';

  const socialLinks = [
    { key: 'instagram', url: s.instagramUrl, icon: <Instagram size={18} />, label: 'Instagram' },
    { key: 'youtube', url: s.youtubeUrl, icon: <Youtube size={18} />, label: 'YouTube' },
    { key: 'facebook', url: s.facebookUrl, icon: <Facebook size={18} />, label: 'Facebook' },
    { key: 'twitter', url: s.twitterUrl, icon: <Twitter size={18} />, label: 'Twitter' },
    { key: 'amazon', url: s.amazonUrl, icon: <MarketplaceIcon name="amazon" />, label: 'Amazon' },
    { key: 'flipkart', url: s.flipkartUrl, icon: <MarketplaceIcon name="flipkart" />, label: 'Flipkart' },
    { key: 'meesho', url: s.meeshoUrl, icon: <MarketplaceIcon name="meesho" />, label: 'Meesho' },
    { key: 'indiamart', url: s.indiamartUrl, icon: <MarketplaceIcon name="indiamart" />, label: 'IndiaMART' },
  ].filter(l => l.url);

  const openLink = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <footer className="bg-[#0A192F] text-white mt-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <h2 className="text-3xl font-bold mb-4" style={{fontFamily: 'Playfair Display, serif'}}>ZINDAL</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">Premium lighting solutions for every occasion.</p>
            {/* Also Available On */}
            {socialLinks.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Find us on</p>
                <div className="flex flex-wrap gap-2">
                  {socialLinks.map(l => (
                    <button key={l.key} onClick={() => openLink(l.url)}
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#D4AF37] flex items-center justify-center text-gray-400 hover:text-white transition-all"
                      data-testid={`footer-${l.key}-link`} aria-label={l.label} title={l.label}>
                      {l.icon}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/products" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">All Products</Link></li>
              <li><Link to="/bulk-inquiry" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">Bulk Orders</Link></li>
              <li><Link to="/track-order" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">Track Order</Link></li>
              <li><Link to="/login" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">Login</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Categories</h3>
            <ul className="space-y-2">
              <li><Link to="/products?category=Festival Lighting" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">Festival Lighting</Link></li>
              <li><Link to="/products?category=Decorative Lighting" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">Decorative Lighting</Link></li>
              <li><Link to="/products?category=Flood & Industrial Lighting" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">Industrial Lighting</Link></li>
              <li><Link to="/products?category=LED Panels" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">LED Panels</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-sm">
                <Phone size={16} className="text-gray-400 flex-shrink-0" />
                <a href={`tel:+91${phone.replace(/\D/g, '')}`} className="text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-phone-link">+91 {phone}</a>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Mail size={16} className="text-gray-400 flex-shrink-0" />
                <a href={`mailto:${email}`} className="text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-email-link">{email}</a>
              </li>
              <li className="flex items-center gap-2 text-gray-400 text-sm">
                <MapPin size={16} className="flex-shrink-0" />
                <span>India</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-12 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; {new Date().getFullYear()} ZINDAL. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};
